import { useAction, useAtom } from '@reatom/npm-react'
import React from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { isLoggedInAtom } from '@root/entities/auth'
import { ThemeDialogButton } from '@root/entities/theme/ui/theme-dialog-button'
import {
  policyScreenAction,
  type SettingScreenPolicy,
} from '@root/features/settings/settings-screen'
import { UpdateButton } from '@root/features/updater'
import { WalletAlertsButton } from '@root/features/wallet-tracker/ui/wallet-alerts-dialog'
import { isTauriAtom, PREMIUM_ENABLED } from '@root/shared'

import { DevStatus } from './dev-status/dev-status'
import { PremiumFeedButton } from './premium-feed-button/premium-feed-button'
import { StatusBarFps } from './status-bar-fps'
import { StatusBarLatency } from './status-bar-latency'
import { StatusBarPrices } from './status-bar-prices'
import { SupportButton } from './support/support-button'

import type { ClassNameProps } from '@root/shared/react'

type StatusBarProps = {
  withPremiumFeed?: boolean
  withTrackerAlerts?: boolean
} & ClassNameProps

export const StatusBar: React.FC<StatusBarProps> = ({
  className,
  withPremiumFeed = true,
  withTrackerAlerts = true,
}) => {
  const policyScreen = useAction(policyScreenAction)
  const [isLoggedIn] = useAtom(isLoggedInAtom)
  const [isTauri] = useAtom(isTauriAtom)

  const { t } = useTranslation()

  const handlePolicyClick = (policy: SettingScreenPolicy) =>
    policyScreen(policy)

  return (
    <Root className={className}>
      <Start>
        {isLoggedIn ? <DevStatus /> : undefined}
        {withTrackerAlerts && isLoggedIn ? <WalletAlertsButton /> : undefined}
        {withPremiumFeed && PREMIUM_ENABLED ? <PremiumFeedButton /> : undefined}
        {isTauri ? <UpdateButton /> : undefined}
        <StatusBarPrices />
      </Start>

      <End>
        <StatusBarTerms>
          <span onClick={() => handlePolicyClick('terms-of-service')}>
            {t('policies.terms-of-service')}
          </span>
          <Separator />
          <span onClick={() => handlePolicyClick('privacy-policy')}>
            {t('policies.privacy-policy')}
          </span>
          {isLoggedIn ? (
            <>
              <Separator />
              <SupportButton />
            </>
          ) : undefined}
        </StatusBarTerms>
        <ThemeDialogButton />
        <StatusBarLatency />
        <StatusBarFps />
      </End>
    </Root>
  )
}

const Root = styled.div`
  height: 24px;
  background: var(--background-1, #111);
  align-items: center;
  display: flex;
  gap: 2px;
  padding-inline: 60px 8px;

  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 100;
  pointer-events: auto;
`

const Start = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
`

const End = styled.div`
  height: 100%;
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 4px;
`

const StatusBarTerms = styled.div`
  display: grid;
  grid-auto-flow: column;
  gap: 8px;

  justify-content: center;
  justify-items: center;
  align-items: center;
  padding-inline: 4px;

  span {
    cursor: pointer;
    color: var(--text-7);

    font-size: 10px;
    font-style: normal;
    font-weight: 500;
    line-height: 12px;

    transition: color 0.15s ease-in-out;

    &:hover {
      color: var(--text-8);
    }
  }
`

const Separator = styled.span`
  width: 1px;
  height: 10px;
  background: var(--backgroundA-3);
`
